/**
 * Portfolio Overview Component
 * Main dashboard component displaying portfolio summary, allocation, and performance
 */

import React, { useEffect, useCallback, memo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { usePortfolioStore } from '../stores/portfolioStore';
import { useAssetStore } from '../stores/assetStore';
import { useLoadingState } from '../hooks/useLoadingState';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { usePortfolioSync } from '../hooks/usePortfolioSync';
import { Card } from './Card';
import { Button } from './Button';
import { LoadingCard } from './LoadingCard';
import { ErrorDisplay } from './ErrorDisplay';
import { PortfolioSummaryCard } from './PortfolioSummaryCard';
import { PerformanceMetrics } from './PerformanceMetrics';
import { HoldingsList } from './HoldingsList';

interface PortfolioOverviewProps {
  className?: string;
}

const PortfolioOverviewComponent: React.FC<PortfolioOverviewProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const { 
    portfolio, 
    isLoading, 
    error, 
    initializePortfolio, 
    syncHoldingsFromAssets,
    updatePortfolioFromPrices,
    setError 
  } = usePortfolioStore();
  
  const { assets } = useAssetStore();
  const { loading: assetsLoading } = useLoadingState({ key: 'assets' });
  const { handleError } = useErrorHandler();
  const { refreshPrices } = usePortfolioSync({
    autoRefresh: false, // Disable auto-refresh to prevent infinite loops
    refreshInterval: portfolio?.config.refreshInterval ?? 30,
  });

  // Initialize portfolio if it doesn't exist
  useEffect(() => {
    if (!portfolio) {
      initializePortfolio('My Portfolio', 'Personal investment portfolio');
    }
  }, [portfolio, initializePortfolio]);

  // Sync holdings from assets to portfolio
  // Only sync when assets change, not when portfolio changes (to avoid infinite loop)
  useEffect(() => {
    if (!portfolio) return;
    
    syncHoldingsFromAssets(assets);
  }, [assets, syncHoldingsFromAssets]); // portfolio is intentionally NOT in dependencies

  // 当切换到 Portfolio 页面时，从 assetStore 读取最新价格并更新 portfolio
  useEffect(() => {
    if (!portfolio || portfolio.holdings.length === 0) return;
    
    // 从 assetStore 读取 Portfolio 相关资产的最新价格
    const assetPrices: Record<string, number> = {};
    portfolio.holdings.forEach(holding => {
      const assetFromStore = assets.find(a => a.id === holding.asset.id);
      if (assetFromStore?.price !== undefined) {
        assetPrices[holding.asset.id] = assetFromStore.price;
      }
    });
    
    // 如果有价格数据，更新 portfolio（异步）
    if (Object.keys(assetPrices).length > 0) {
      updatePortfolioFromPrices(assetPrices).catch(console.error);
    }
  }, []); // 空依赖数组，只在组件挂载时执行一次

  // Handle loading states
  const showLoading = isLoading || assetsLoading;

  // Handle errors - memoized callback
  const handleErrorCallback = useCallback(() => {
    if (error) {
      handleError(new Error(error), 'portfolio-overview');
    }
  }, [error, handleError]);

  useEffect(() => {
    handleErrorCallback();
  }, [handleErrorCallback]);

  // Memoized retry handler
  const handleRetry = useCallback(() => {
    setError(null);
    refreshPrices();
  }, [setError, refreshPrices]);

  // Memoized create portfolio handler
  const handleCreatePortfolio = useCallback(() => {
    initializePortfolio('My Portfolio', 'Personal investment portfolio');
  }, [initializePortfolio]);

  if (showLoading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <LoadingCard />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <LoadingCard />
          <LoadingCard />
        </div>
        <LoadingCard />
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <Card className="p-6">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <Button onClick={handleRetry}>Retry</Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!portfolio) {
    return (
      <div className={`flex items-center justify-center min-h-64 ${className}`}>
        <Card className="p-8 text-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No Portfolio Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Create your first portfolio to get started with tracking your investments.
          </p>
          <button
            onClick={handleCreatePortfolio}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Create Portfolio
          </button>
        </Card>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`space-y-6 ${className}`}
    >
      {/* Portfolio Summary */}
      <PortfolioSummaryCard 
        summary={portfolio.summary}
        config={portfolio.config}
      />

      {/* Holdings List */}
      {portfolio.holdings.length > 0 && (
        <HoldingsList 
          holdings={portfolio.holdings}
          config={portfolio.config}
        />
      )}

      {/* Performance Metrics - Full Width */}
      <PerformanceMetrics 
        performance={portfolio.performance}
        config={portfolio.config}
      />

      {/* Empty State for Holdings */}
      {portfolio.holdings.length === 0 && (
        <Card className="p-8 text-center">
          <div className="text-gray-400 dark:text-gray-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('portfolio.noHoldingsYet')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('portfolio.noHoldingsMessage')}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('portfolio.goToAssetsTab')}
          </p>
        </Card>
      )}
    </motion.div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const PortfolioOverview = memo(PortfolioOverviewComponent);